/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { MtaLearningProductInformationDTO } from '@mta/vela-api-types'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import { BasketItemWithProductInformation, BasketState } from '@/store/basket/types'

/** Initial empty basket state. */
const initialBasketState: BasketState = {
  items: [],
}

/** Base atom for storing the state of the basket. */
const basketAtom = atomWithStorage<BasketState>('shop:basket', initialBasketState, undefined, { getOnInit: true })

/** Atom to get the total number of unique items in the basket */
const basketItemCountAtom = atom((get) => get(basketAtom).items.length)

/** Atom to get the total quantity of all items in the basket */
const basketTotalQuantityAtom = atom((get) => get(basketAtom).items.reduce((total, item) => total + item.quantity, 0))

/** Atom to hold the list of all available products fetched from the API. */
const productsAtom = atom<Array<MtaLearningProductInformationDTO>>([])

/** Derived atom that combines basket items with their detailed product information. */
const basketItemsWithProductInfoAtom = atom<Array<BasketItemWithProductInformation>>((get) => {
  const basket = get(basketAtom)
  const products = get(productsAtom)
  return basket.items.map((item) => ({
    ...item,
    details: products.find((p) => p.identifier === item.productId),
  }))
})

/** Atom debug labels. */
basketAtom.debugLabel = 'basketAtom'
basketItemCountAtom.debugLabel = 'basketItemCountAtom'
basketTotalQuantityAtom.debugLabel = 'basketTotalQuantityAtom'
basketItemsWithProductInfoAtom.debugLabel = 'basketItemsWithProductInfoAtom'
productsAtom.debugLabel = 'productsAtom'

export {
  basketAtom,
  basketItemCountAtom,
  basketItemsWithProductInfoAtom,
  basketTotalQuantityAtom,
  initialBasketState,
  productsAtom,
}
